<template>
    <CollapsibleSidebar ref="sidebar" class="category-sidebar-shell">
        <template v-slot:content>
            <Notification v-if="hasError" class="is-danger">
                {{ translate("category_sidebar_shell.load_error") }}
            </Notification>

            <template v-else>
                <div class="category-sidebar-shell__category-name">
                    <template v-if="summary">{{ summary.name }}</template>
                    <InlineSpinner v-else />

                    <span class="is-inline-flex is-align-items-center is-gap-1">
                        <span
                            v-if="summary"
                            class="tag is-dark category-sidebar-shell__competitors-count"
                            :title="translate('category_sidebar_shell.competitors_count')"
                        >
                            <span class="icon"><i class="fas fa-users"></i></span>
                            <span>{{ summary.competitorsCount }}</span>
                        </span>
                        <BracketTypeBadge v-if="bracketTypeBadgeFight" :fight="bracketTypeBadgeFight" />
                    </span>
                </div>

                <div v-if="view === 'menu'" class="category-sidebar-shell__tiles">
                    <template v-for="tile in tiles" :key="tile.title">
                        <component
                            :is="tile.action === 'navigate' && !isTileDisabled(tile) ? 'a' : 'button'"
                            :id="tile.id"
                            :type="tile.action === 'navigate' && !isTileDisabled(tile) ? undefined : 'button'"
                            :href="tile.action === 'navigate' && !isTileDisabled(tile) ? tile.href : undefined"
                            :disabled="isTileDisabled(tile)"
                            class="category-sidebar-shell__tile"
                            :class="{ 'category-sidebar-shell__tile--disabled': isTileDisabled(tile) }"
                            @click="onTileClick(tile)"
                        >
                            <span class="icon category-sidebar-shell__tile-icon">
                                <i :class="tile.icon"></i>
                            </span>
                            <span class="category-sidebar-shell__tile-text">
                                <span class="category-sidebar-shell__tile-title">{{ tile.title }}</span>
                                <span class="category-sidebar-shell__tile-subtitle">
                                    {{ isTileDisabled(tile) && tile.disabledSubtitle ? tile.disabledSubtitle : tile.subtitle }}
                                </span>
                            </span>
                            <span class="icon category-sidebar-shell__tile-chevron">
                                <i class="fas fa-chevron-right"></i>
                            </span>
                        </component>
                    </template>
                </div>

                <div v-else-if="activeTile" class="category-sidebar-shell__tile-view">
                    <button type="button" class="category-sidebar-shell__back" @click="backToMenu">
                        <span class="icon"><i class="fas fa-arrow-left"></i></span>
                        <span>{{ translate("common.back") }}</span>
                    </button>

                    <component :is="activeTile.content" v-bind="{ ...activeTile.contentProps, parameterId: currentParameterId }" />
                </div>
            </template>
        </template>
    </CollapsibleSidebar>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import CollapsibleSidebar from "./CollapsibleSidebar.vue";
    import InlineSpinner from "../InlineSpinner.vue";
    import Notification from "../Notification.vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { translate } from "../translations";
    import categoriesApi, { CategorySidebarSummary } from "../../Api/Categories/categories";
    import { handleError } from "../errorHandler";
    import BracketTypeBadge from "../BracketTypeBadge.vue";
    import type { CategorySidebarTile } from "./categorySidebarTypes";

    const props = defineProps<{ tiles: CategorySidebarTile[]; eventId: number }>();

    const sidebar = ref<ComponentExposed<typeof CollapsibleSidebar> | null>(null);
    const view = ref<"menu" | "tile">("menu");
    const activeTile = ref<CategorySidebarTile | null>(null);
    const currentParameterId = ref<number | null>(null);
    const summary = ref<CategorySidebarSummary | null>(null);
    const hasError = ref(false);
    const bracketTypeBadgeFight = computed(() =>
        summary.value?.bracketType === null || summary.value?.bracketType === undefined ? null : { bracketType: summary.value.bracketType },
    );

    function open(parameterId: number) {
        currentParameterId.value = parameterId;
        activeTile.value = null;
        view.value = "menu";
        summary.value = null;
        hasError.value = false;
        sidebar.value?.openSidebar();

        categoriesApi
            .getCategorySidebarSummary(props.eventId, parameterId)
            .then(response => {
                if (currentParameterId.value === parameterId) {
                    summary.value = response;
                }
            })
            .catch(err => {
                if (currentParameterId.value === parameterId) {
                    hasError.value = true;
                }
                handleError(err);
            });
    }

    function close() {
        sidebar.value?.closeSidebar();
    }

    defineExpose({ open, close });

    function isTileDisabled(tile: CategorySidebarTile): boolean {
        if (!tile.isDisabled) {
            return false;
        }

        return summary.value === null || tile.isDisabled(summary.value);
    }

    function onTileClick(tile: CategorySidebarTile) {
        if (isTileDisabled(tile)) {
            return;
        }

        if (tile.action === "inline") {
            activeTile.value = tile;
            view.value = "tile";
        } else if (tile.action === "callback" && tile.onClick && currentParameterId.value !== null) {
            tile.onClick(currentParameterId.value);
        }
    }

    function backToMenu() {
        activeTile.value = null;
        view.value = "menu";
    }
</script>
